import { useQuery } from '@tanstack/react-query';

import type { GetMarkInTransitToFulfillmentCenterResponse } from '@ekhodealer/ekho-common';

import { useCurrentServicesProviderId } from '@common/hooks/useCurrentServicesProviderId';
import { useQueryErrorBoundary } from '@common/hooks/useErrorBoundary';
import { apiClient } from '@config/server/apiClient';
import { ordersEndpoints } from '@config/server/apiEndpoints';
import { queries } from '@config/server/queryKeys';

/**
 * API function to get transit to fulfillment center data from a purchased vehicle
 */
export const getMarkInTransitToFulfillmentCenter = async (
	servicesProviderId: string,
	orderId: string,
): Promise<GetMarkInTransitToFulfillmentCenterResponse> => {
	const response = await apiClient.get<GetMarkInTransitToFulfillmentCenterResponse>(
		ordersEndpoints.markInTransitToFulfillmentCenter(servicesProviderId, orderId),
	);
	return response.data;
};

/**
 * Hook for getting transit to fulfillment center data from a purchased vehicle
 * @param orderId - The purchased vehicle ID (orderId in frontend terms)
 */
export const useGetMarkInTransitToFulfillmentCenter = (orderId: string | undefined) => {
	const errorBoundaryOptions = useQueryErrorBoundary();
	const servicesProviderId = useCurrentServicesProviderId();

	return useQuery({
		queryKey: queries.orders.detail(servicesProviderId, orderId || '')._ctx.progress._ctx
			.markInTransitToFulfillmentCenter.queryKey,
		queryFn: () => {
			if (!orderId) {
				throw new Error(
					'Unable to get transit to fulfillment center data without an order ID.',
				);
			}
			return getMarkInTransitToFulfillmentCenter(servicesProviderId, orderId);
		},
		enabled: Boolean(orderId) && Boolean(servicesProviderId),
		staleTime: Infinity,
		...errorBoundaryOptions,
	});
};
